import type { ActivitiesProps } from './division-form-activities.interface';
import type { DragOverEvent } from '@dnd-kit/core';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FieldArray } from 'formik';
import React from 'react';

import { DivisionFormActivitiesItem } from './division-form-activities.item';
import { usePermissions } from '../../../../contexts/permissions.context';
import { vocabulary } from '../../../../vocabulary/german';
import { CreateButton } from '../../../buttons/create-button/create-button';
import { Container } from '../../../template/container/container';

const DivisionFormActivities: React.FC<ActivitiesProps> = ({
  currentYearFrozen = false,
  ...props
}: ActivitiesProps) => {
  const matrix = usePermissions();
  const currentYear = props.yearName ? +props.yearName : 0;
  const lastYear = currentYear - 1;
  const beforeLastYear = lastYear - 1;

  const isTouchDevice =
    typeof window !== 'undefined' && 'ontouchstart' in window;
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 200, tolerance: 5 },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);

  return (
    <FieldArray
      name={'Activities'}
      render={(activitiesHelper) => {
        const handleDragOver = (event: DragOverEvent) => {
          const { active, over } = event;
          if (!over || active.id === over.id) {
            return;
          }
          const oldIndex = (props.activities || []).findIndex(
            (item) => active.id === item.id,
          );
          const newIndex = (props.activities || []).findIndex(
            (item) => over.id === item.id,
          );
          if (oldIndex !== -1 && newIndex !== -1) {
            activitiesHelper.move(oldIndex, newIndex);
          }
        };

        const handleDragEnd = () => {
          if (props.activities && props.activities.length > 0) {
            props.activities.forEach((_, index) => {
              activitiesHelper.form.setFieldValue(
                `Activities.${index}.order`,
                index + 1,
              );
            });
          }
        };

        return (
          <div>
            <div className={'form-panel-gray'}>
              <Container>
                <div className="row">
                  <div className="activity-col">
                    <p className={'form-panel-gray__text'}>
                      {vocabulary.labels.activities}
                    </p>
                  </div>
                  <div className="activity-col">
                    <p className={'form-panel-gray__text'}>{beforeLastYear}</p>
                  </div>
                  <div className="activity-col">
                    <p className={'form-panel-gray__text'}>{lastYear}</p>
                  </div>
                  <div className="activity-col">
                    <p className={'form-panel-gray__text'}>{currentYear}</p>
                  </div>
                  <div className="activity-col">
                    <p className={'form-panel-gray__text'}>
                      {vocabulary.forms.division.tableLabels.controllable}
                    </p>
                  </div>
                </div>
              </Container>
            </div>

            {props.activities && props.activities.length > 0 ? (
              <>
                <div className="container">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={props.activities}
                      strategy={verticalListSortingStrategy}
                    >
                      {props.activities.map((activity, index) => (
                        <DivisionFormActivitiesItem
                          key={activity.id}
                          activity={activity}
                          index={index}
                          currentYearFrozen={currentYearFrozen}
                          activitiesHelper={activitiesHelper}
                          componentName={DivisionFormActivities.name}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </>
            ) : (
              <Container>
                <p className={'activity__message'}>
                  Nicht gefunden: Aufgaben konnten nicht gefunden werden.
                </p>
              </Container>
            )}

            <Container>
              {(currentYearFrozen
                ? matrix.permissions.freezeAddFieldsInside === true
                : matrix.permissions.addFieldsInside === true) && (
                <CreateButton
                  text={vocabulary.buttons.addActivity}
                  onClick={() => {
                    activitiesHelper.push({
                      id: null,
                      title: '',
                      beforeLastYear: '',
                      lastYear: '',
                      curYear: '',
                      controllable: 1,
                      YearId: props.yearId,
                      order: (props.activities?.length || 0) + 1,
                    });
                  }}
                />
              )}
            </Container>
          </div>
        );
      }}
    />
  );
};

export default DivisionFormActivities;
